import type { IImageUrl } from '@audacy-clients/core/types/images';
import QS from 'qs';
import UrlParser from 'url-parse';

const FASTLY_DEFAULTS = {
  auto: 'webp',
};

const FASTLY_HOSTS = new Set([
  'images.radio.com',
  'images.audacy2-stg.int.audacy.com',
  'images.audacy2-prod.ext.audacy.com',
  'stg-images.radio.com',
  'radioimg.audacy.com',
  'radioimg-nonprod.audacy.com'
]);

export enum ImageType {
  Thumb,
  Full,
}

export const generateImageUrl = (
  url: string,
  size: number,
  type: ImageType,
): string | undefined => {
  const isBase64Image = url.startsWith('data:image/');
  if (isBase64Image) {
    // Return the original Base64 image if it's Base64 encoded
    return url;
  }

  const parsedUrl = new UrlParser(url, true);
  const isFastlyUrl = FASTLY_HOSTS.has(parsedUrl?.host);

  if (isFastlyUrl) {
    // if it's a fastly URL, force the protocol to HTTPS to avoid load issues with legacy images
    parsedUrl.set('protocol', 'https');

    return parsedUrl.toString((q: { height?: number }): string => {
      // Some imageURIs from the CMS can contain width and height parameters among others.
      // Since we will override the width param ourselves, we need to clear existing height
      // to avoid distorted / skewed images.
      q.height && delete q.height;

      return QS.stringify({
        ...FASTLY_DEFAULTS,
        ...q,
        width: size,
      });
    });
  }

  if (type === ImageType.Thumb) {
    return undefined;
  }

  const widthQuery = `?auto=webp&width=${size}`;

  return url + widthQuery;
};

export function areImagesEqual(image1: string | IImageUrl, image2: string | IImageUrl): boolean {
  if (typeof image1 !== typeof image2) {
    return false;
  }

  if (typeof image1 === 'string' && typeof image2 === 'string') {
    return image1 === image2;
  }

  if (typeof image1 === 'object' && typeof image2 === 'object') {
    return image1.src === image2.src && image1.srcFallback === image2.srcFallback;
  }

  return false;
}
